/**
 * VERSION-INDICATOR — seção "Versão" em Configurações.
 *
 * Lê:
 *   - frontend: src/version.json (escrito pelo prebuild — VERCEL_GIT_COMMIT_SHA)
 *   - backend:  GET /api/version (RENDER_GIT_COMMIT)
 *
 * Mostra:
 *   - linha App:      sha curto + builtAt
 *   - linha Servidor: commit_short + started_at
 *   - status:         iguais → verde "Sincronizados"
 *                     diferentes → âmbar "Versões divergentes"
 *                     servidor inacessível → cinza "Servidor inacessível"
 *
 * SHA curto clicável copia o full SHA pra clipboard.
 */
import { useCallback, useEffect, useState } from "react";
import { Check, AlertTriangle, Copy, WifiOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import frontendVersion from "@/version.json";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatTimestamp(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function ShaButton({ fullSha, label }) {
  const handleCopy = useCallback(() => {
    if (!fullSha || fullSha === "dev") return;
    try {
      navigator.clipboard.writeText(fullSha);
      toast.success("SHA copiado");
    } catch {
      toast.error("Não consegui copiar");
    }
  }, [fullSha]);
  const display = (fullSha && fullSha !== "dev") ? fullSha.slice(0, 7) : "dev";
  const copyable = fullSha && fullSha !== "dev";
  return (
    <button
      type="button"
      onClick={handleCopy}
      disabled={!copyable}
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md
                 font-mono text-caption bg-surface-elevated border border-subtle
                 hover:border-brand-cyan/40 disabled:cursor-default disabled:hover:border-subtle
                 transition-colors"
      title={copyable ? `Clique pra copiar ${label} completo` : label}
      data-testid={`version-sha-${label.toLowerCase()}`}
    >
      <span>{display}</span>
      {copyable && <Copy className="w-3 h-3 opacity-60" />}
    </button>
  );
}

export default function VersionSection() {
  const [backend, setBackend] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/version`, {
          method: "GET",
          // Endpoint público — sem Authorization
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) {
          setBackend(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(String(err?.message || err));
          setBackend(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const frontSha = frontendVersion?.sha || "dev";
  const backSha = backend?.commit || "dev";

  // Status comparativo
  let statusKind = "loading"; // loading | sync | diverge | offline
  if (loading) statusKind = "loading";
  else if (error) statusKind = "offline";
  else if (frontSha === "dev" || backSha === "dev") statusKind = "dev";
  else if (frontSha === backSha) statusKind = "sync";
  else statusKind = "diverge";

  return (
    <section className="space-y-6" data-testid="settings-version-section">
      <div>
        <h2 className="text-h2 text-foreground mb-1">Versão</h2>
        <p className="text-caption text-foreground-subtle mb-5">
          Confira se o último deploy do Nexo está ativo no app e no servidor.
        </p>

        <div className="space-y-3 bg-surface-card border border-subtle rounded-xl p-5">
          <div className="flex items-center justify-between gap-4 flex-wrap"
               data-testid="version-row-app">
            <div className="flex items-center gap-3">
              <span className="text-caption text-foreground-muted w-20">App</span>
              <ShaButton fullSha={frontSha} label="App" />
            </div>
            <span className="text-caption text-foreground-subtle">
              build {formatTimestamp(frontendVersion?.builtAt)}
            </span>
          </div>

          <div className="flex items-center justify-between gap-4 flex-wrap"
               data-testid="version-row-server">
            <div className="flex items-center gap-3">
              <span className="text-caption text-foreground-muted w-20">Servidor</span>
              {loading ? (
                <span className="inline-flex items-center gap-1.5 text-caption text-foreground-subtle">
                  <Loader2 className="w-3 h-3 animate-spin" /> carregando…
                </span>
              ) : error ? (
                <span className="text-caption text-foreground-subtle">—</span>
              ) : (
                <ShaButton fullSha={backSha} label="Servidor" />
              )}
            </div>
            <span className="text-caption text-foreground-subtle">
              {error ? "—" : `no ar desde ${formatTimestamp(backend?.started_at)}`}
            </span>
          </div>
        </div>

        {/* Status */}
        <div className="mt-4" data-testid="version-status">
          {statusKind === "sync" && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl
                            bg-emerald-500/10 border border-emerald-500/30">
              <Check className="w-4 h-4 text-emerald-400" />
              <span className="text-caption text-emerald-300">
                Frontend e servidor sincronizados
              </span>
            </div>
          )}
          {statusKind === "diverge" && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl
                            bg-amber-500/10 border border-amber-500/30"
                 data-testid="version-status-diverge">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <span className="text-caption text-amber-300">
                Versões divergentes — um deploy pode ter falhado.
              </span>
            </div>
          )}
          {statusKind === "offline" && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl
                            bg-surface-elevated border border-subtle">
              <WifiOff className="w-4 h-4 text-foreground-subtle" />
              <span className="text-caption text-foreground-muted">
                Servidor inacessível — não consegui buscar a versão do backend.
              </span>
            </div>
          )}
          {statusKind === "dev" && !loading && !error && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl
                            bg-surface-elevated border border-subtle">
              <span className="text-caption text-foreground-muted">
                Build de desenvolvimento — comparação de versão indisponível.
              </span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
